import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { catchError, EMPTY, Subject, Subscription, switchMap } from 'rxjs';
import scrollIntoView from 'scroll-into-view-if-needed';

import { ToastMessageType } from 'Angular/features/components/toast-messages/toast-messages.enum';
import {
    GlossaryCategory,
    GlossaryData,
    GlossaryFilters,
    GlossaryTerm,
} from 'GlossaryApp/ts/interfaces/glossary-term.interface';

import { PageToastMessagesService } from 'Angular/features/components/toast-messages/page-toast-messages-service';
import { UrlsService } from 'Angular/services/core/urls.service';
import { GlossaryService } from 'GlossaryApp/ts/services/glossary.service';

@Component({ standalone: false, selector: 'ycn-glossary-index', templateUrl: 'glossary-index.template.html' })
export class GlossaryIndexComponent implements OnInit, OnDestroy {
    readonly alphabet: string[] = ['#', ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))];

    letters: string[] = [];

    terms: { [k: string]: GlossaryTerm[] } = {};

    categories: GlossaryCategory[] = [];

    selectedCategory: string = '';

    selectedParentCategory: string | null = null;

    private readonly filterSubject$ = new Subject<Partial<GlossaryFilters>>();

    private subscription: Subscription | null = null;

    constructor(
        private readonly eltRef: ElementRef,
        private urlsService: UrlsService,
        private glossaryService: GlossaryService,
        private toastMessagesService: PageToastMessagesService
    ) {
        const categories = JSON.parse(this.eltRef.nativeElement.getAttribute('categories')) ?? [];
        if (categories.length > 0) {
            this.categories = [{ internalName: '', name: 'All', subcategories: [] }, ...categories];
        }
    }

    ngOnInit(): void {
        this.subscription = this.filterSubject$
            .pipe(
                switchMap((filters: Partial<GlossaryFilters>) => {
                    return this.glossaryService.getGlossaryData(filters).pipe(
                        catchError(() => {
                            this.toastMessagesService.addMessage('Error fetching data', ToastMessageType.ERROR);
                            return EMPTY;
                        })
                    );
                })
            )
            .subscribe((data: GlossaryData) => {
                this.letters = data.letters;
                this.terms = data.terms;
            });
        this.filterSubject$.next({});
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe(); // eslint-disable-line no-unused-expressions
    }

    filterByCategory(category: string, parentCategory: string | null = null): void {
        this.selectedCategory = category;
        this.selectedParentCategory = parentCategory;
        const filters: Partial<GlossaryFilters> = {};
        if (category !== '') {
            filters.category = category;
        }
        this.filterSubject$.next(filters);
    }

    navigateToDefinitionPage(term: string): void {
        if (term !== undefined) {
            window.location.href = this.urlsService.getUrl(this.urlsService.GLOSSARY.GLOSSARY_TERM, { term });
        }
    }

    scrollToLetter(letter: string): void {
        setTimeout(() => {
            const querySelector = `section-${letter}`;
            const target = document.getElementById(querySelector);
            if (target) {
                scrollIntoView(target, { behavior: 'smooth', block: 'start' });
            }
        }, 0);
    }
}
